import { Fragment } from "react";

import { Condition, Procedure } from "@medplum/fhirtypes";

import { ActionIcon, Group, Paper, Stack, Text, UnstyledButton } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";

import {
  IconChevronDown,
  IconChevronUp,
  IconCornerDownRight,
  IconEdit,
  IconNote,
  IconReportMedical,
  IconTrash,
} from "@tabler/icons-react";

import { TextWrapper } from "@/components/TextWrapper";
import {
  ProceduresTabConditionData,
  ProceduresTabProcedureData,
} from "@/encounters/types/procedures";
import { groupEncounterDiagnoses } from "@/encounters/utils/encounter-diagnoses";

import { NoDataContainer } from "../../NoDataContainer";

import ProceduresCodeBadge from "./ProceduresCodeBadge";
import classes from "./ProceduresTabConditions.module.css";

interface ProceduresTabConditionsProps {
  procedures: Procedure[];
  conditions: Condition[];
  onEdit: (condition: Condition, procedures: Procedure[]) => void;
  onDelete: (condition: Condition, procedures: Procedure[]) => void;
  /** Editing stages work for the encounter's save, so the actions are hidden when there is none. */
  canEditProcedures: boolean;
}

interface ConditionCardProps {
  condition: ProceduresTabConditionData;
  procedures: ProceduresTabProcedureData[];
  onEdit: () => void;
  onDelete: () => void;
  canEditProcedures: boolean;
}

function ConditionCard({
  condition,
  procedures,
  onEdit,
  onDelete,
  canEditProcedures,
}: ConditionCardProps) {
  const [opened, { toggle }] = useDisclosure(true);

  return (
    <Paper p="lg" bg="white" radius="md" bd="1px solid var(--mantine-color-cream-4)">
      <Group wrap="nowrap" justify="space-between">
        <Group gap="sm" wrap="nowrap">
          <ActionIcon
            size="md"
            color="dark"
            onClick={toggle}
            variant="transparent"
            data-testid="procedures-tab-condition-toggle-button"
            aria-label={opened ? "Collapse condition" : "Expand condition"}
          >
            {opened ? <IconChevronUp size={24} /> : <IconChevronDown size={24} />}
          </ActionIcon>
          <ProceduresCodeBadge code={condition.code} />
          <TextWrapper fw={600}>{condition.display}</TextWrapper>
        </Group>
        {canEditProcedures && (
          <Group gap="lg" wrap="nowrap">
            <UnstyledButton onClick={onDelete} data-testid="procedures-tab-condition-delete-button">
              <Group gap={8} wrap="nowrap">
                <IconTrash size={24} color="var(--mantine-color-red-7)" />
                <TextWrapper fw={600} c="red.7">
                  Delete
                </TextWrapper>
              </Group>
            </UnstyledButton>
            <UnstyledButton onClick={onEdit} data-testid="procedures-tab-condition-edit-button">
              <Group gap={8} wrap="nowrap">
                <IconEdit size={24} color="var(--mantine-color-burgundy-9)" />
                <TextWrapper fw={600} c="burgundy.9">
                  Edit
                </TextWrapper>
              </Group>
            </UnstyledButton>
          </Group>
        )}
      </Group>

      {opened && (
        <Stack pl={36} pt="md" gap="sm">
          {/* A diagnosis can be recorded on its own. Its card offers the same picker as Edit, so
              the provider can add procedures under it. */}
          {!procedures.length && (
            <NoDataContainer
              onAnchorClick={onEdit}
              linkText="Add Procedure"
              hideAnchor={!canEditProcedures}
              text="No procedures have been added."
              actionTestId="procedures-tab-condition-add-procedure-button"
              icon={<IconReportMedical size={18} color="var(--mantine-color-gray-8)" />}
            />
          )}
          {procedures.map((procedure) => (
            <Fragment key={procedure.id ?? procedure.code}>
              <Group gap="sm" wrap="nowrap">
                <IconCornerDownRight size={24} />
                <ProceduresCodeBadge code={procedure.code} />
                <TextWrapper>{procedure.display}</TextWrapper>
              </Group>
              {procedure.providerNotesHtml && (
                <Group gap="sm" wrap="nowrap" align="flex-start">
                  <IconNote size={24} />
                  <Text
                    component="div"
                    data-testid="procedure-note"
                    className={classes.procedureNote}
                    dangerouslySetInnerHTML={{ __html: procedure.providerNotesHtml }}
                  />
                </Group>
              )}
              {procedure.billerNotes && (
                <Group gap="sm" wrap="nowrap" align="flex-start">
                  <IconNote size={24} />
                  <TextWrapper style={{ whiteSpace: "pre-wrap" }}>
                    {procedure.billerNotes}
                  </TextWrapper>
                </Group>
              )}
            </Fragment>
          ))}
        </Stack>
      )}
    </Paper>
  );
}

function ProceduresTabConditions({
  procedures: procedureResources,
  conditions: conditionResources,
  onEdit,
  onDelete,
  canEditProcedures,
}: ProceduresTabConditionsProps) {
  // Each card shows only the procedures linked to its condition (via reasonReference), so a
  // condition's procedures stay isolated from the other conditions on the encounter.
  const diagnoses = groupEncounterDiagnoses(conditionResources, procedureResources);

  return (
    <Stack gap="md">
      {diagnoses.map(({ condition, procedures, resources }) => (
        <ConditionCard
          condition={condition}
          procedures={procedures}
          key={condition.id ?? condition.code}
          canEditProcedures={canEditProcedures}
          onEdit={() => onEdit(resources.condition, resources.procedures)}
          onDelete={() => onDelete(resources.condition, resources.procedures)}
        />
      ))}
    </Stack>
  );
}

export default ProceduresTabConditions;
